import { useLocation } from 'react-router';
import { useState, useEffect } from 'react';
import { alpha, styled, lighten } from '@mui/material/styles';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import PropTypes from 'prop-types';
import { Box, IconButton, Pagination, Select, MenuItem, Stack } from '@mui/material';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { Icon as Iconify } from '@iconify/react';
import { useModuleNavigate, useModuleListPath } from '../../layouts/main/ModuleNavigation';
import useModuleStorage from '../../store/useModuleStorage';


export default function PostedFilterDataGrid({ data, headerName }) {
    
    const ODD_OPACITY = 0.2;

    const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
        [`& .${gridClasses.columnHeader}`]: {
            backgroundColor: alpha(lighten(theme.palette.primary.main, 0.5), ODD_OPACITY),
            color: '#023A82',
            paddingLeft: '10px',
            fontWeight: 'bold'
        },
        [`& .${gridClasses.row}.even`]: {
            backgroundColor: '#e6eef6',

            '&:hover, &.Mui-hovered': {
                backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
                '@media (hover: none)': {
                    backgroundColor: 'transparent',
                },
            },
            '&.Mui-selected': {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    ODD_OPACITY + theme.palette.action.selectedOpacity,
                ),
                '&:hover, &.Mui-hovered': {
                    backgroundColor: alpha(
                        theme.palette.primary.main,
                        ODD_OPACITY +
                        theme.palette.action.selectedOpacity +
                        theme.palette.action.hoverOpacity,
                    ),
                    '@media (hover: none)': {
                        backgroundColor: alpha(
                            theme.palette.primary.main,
                            ODD_OPACITY + theme.palette.action.selectedOpacity,
                        ),
                    },
                },
            },
        },
        [`& .${gridClasses.row}.odd`]: {
            backgroundColor: 'white',

            '&:hover, &.Mui-hovered': {
                backgroundColor: "#F7F7FA",
                '@media (hover: none)': {
                    backgroundColor: 'transparent',
                },
            },

        },

        '& .MuiDataGrid-cell': {
            alignItems: 'start',
        },
        [`& .MuiDataGrid-row`]: {
            // paddingBottom:'0px'
            padding: '10px 0px'
        },

        [`& .bold-header`]: {
            fontWeight: 'bold',
        },


    }));

    // eslint-disable-next-line no-unused-vars
    const [pageSize, setPageSize] = useState(6);
    const [currentPage, setCurrentPage] = useState(1);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const navigate = useModuleNavigate();
    const moduleListPath = useModuleListPath();
    const moduleStorage = useModuleStorage();
    const location = useLocation();


    const handlePageChange = (event, newPage) => {
        setCurrentPage(newPage);
    };

    const downloadReferenceDocuments = async (documentList) => {
        const documents = documentList.split(',');
        const files = documents.map((document) => {
            const path = document.split('\\').pop();
            return {
                path,
                contentUrl: document,
            };
        });

        const zip = new JSZip();
        const fetchFileContents = async (file) => {
            const response = await fetch(file.contentUrl);
            const content = await response.blob();
            return content;
        };

        const fileContents = await Promise.all(files.map(fetchFileContents));

        fileContents.forEach((content, index) => {
            zip.file(files[index].path, content, { binary: true });
        });

        zip.generateAsync({ type: "blob" }).then((blob) => {
            saveAs(blob, "ReferenceDocuments.zip");
        });
    };


    const uniqueColumns = Array.from(
        new Map(
            (data?.ColumnWidth || []).map((column) => [column.ColumnName, column]) // Handle empty data case
        ).values()
    );

    const columns = uniqueColumns
        .filter((column) => column.ColumnName !== 'Id') // Exclude the "id" column
        .map((column) => ({
            field: column.ColumnName,
            headerName: column.ColumnName,
          //  flex: column.ColumnWidth,
            flex: parseInt(column.ColumnWidth, 10) * 10,
            renderCell: (params) => (
                <div style={{ whiteSpace: "normal", wordBreak: "break-word", display: 'flex', alignItems: 'center' }}>
                    {params.value}
                </div>
            ),
        }));

    const getColumnWidth = (header) => {
        const minWidth = 100;
        const charWidth = 10; // Approximate width per character
        return Math.max(minWidth, header.length * charWidth);
    };

    const rows = data.Data?.map((row) => ({
        id: Number(row.Id),
        ...row,
    })) || []; // Handle empty data case
    if (rows.length > 0) {
        columns.push({
            field: 'download',
            headerName: 'Download',
            width: getColumnWidth('Download'),
            renderCell: (params) => (
                <IconButton onClick={(event) => {
                    downloadReferenceDocuments(params.row.Documents);
                }} size='small'>
                    <Iconify icon="bx:download" width={20} height={20} />
                </IconButton>
            ),
        });
    }




    // Function to get displayed rows with a shift
    function shiftDataToStart(rows1, startIndex1, endIndex1) {
        const selectedRows = rows1.slice(startIndex1, endIndex1);

        const beforeSelected = rows1.slice(0, startIndex1);

        const afterSelected = rows1.slice(endIndex1);
        let returnData;
        if (selectedRows.length % pageSize === 0) {

            returnData = selectedRows.concat(beforeSelected, afterSelected);
        } else {

            returnData = selectedRows
        }
        return returnData;

    }

    const updatedRows = shiftDataToStart(rows, startIndex, endIndex);

    useEffect(() => {
        const storedPageNumber = JSON.parse(moduleStorage.getItem('gridPageNumber'));
        if (storedPageNumber && storedPageNumber.currentUrl === location.pathname) {
            setCurrentPage(storedPageNumber.pageNumber);
        } else {
            setCurrentPage(1);
        }
    }, [data, location.pathname, moduleStorage]);

    const handleRowDoubleClick = (row) => {
        moduleStorage.setItem('gridPageNumber', JSON.stringify({ currentUrl: location.pathname, pageNumber: currentPage }));
        navigate(`${moduleListPath}/edit`, { state: { data: { id: row.id, pageName: headerName } } });

    };
    return (
        <Box sx={{ height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', }}>
            <Box sx={{ width: "100%", display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflowX: 'auto' }}>
                <style>
                    {
                        `.css-191bxd1-MuiDataGrid-columnHeaderTitle {
                        font-weight: 700;
                        }`
                    }
                </style>
                <StripedDataGrid  localeText={{ noRowsLabel: 'Data not found' }}
                    rows={updatedRows}
                    columns={columns}
                    pageSize={pageSize}
                        sx={{
                            height: '100%',
                            '& .css-1vjh543-MuiDataGrid-footerContainer': {
                                // border: 0,
                                minHeight: 0
                            },
                            '& .css-1t5atbe-MuiDataGrid-root .MuiDataGrid-row': {
                                paddingBottom: '0px',
                                height: 'fit-content', // Adjust row height based on content
                            },
                            '& .css-1t5atbe-MuiDataGrid-root .MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight) > .MuiDataGrid-cell': {
                                whiteSpace: 'pre-wrap', // Proper white-space handling for wrapping
                                lineHeight: 1,
                                wordWrap: 'break-word',
                            }
                        }}
                    disableSelectionOnClick
                    getRowId={(row) => row.id}
                    getRowClassName={(params) =>
                        params.indexRelativeToCurrentPage % 2 === 0 ? 'odd' : 'even'
                    }

                    onRowDoubleClick={(params) => handleRowDoubleClick(params.row)}
                    components={{ Pagination: null }}
                    hideFooterPagination
                        getRowHeight={() => "auto"}
                        autoPageSize                /> 
                </Box>
            </Box>
            <div style={{ display: 'flex' }}>

                {updatedRows.length > 0 &&
                    <div style={{ justifySelf: 'flex-end' }} >
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            spacing={2}
                            sx={{ mb: 0, pt: 2 }}
                        >
                            <Pagination
                                count={Math.ceil(rows.length / pageSize)}
                                variant="outlined"
                                size="small"
                                shape="rounded"
                                page={currentPage}
                                onChange={handlePageChange}
                            />
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <span style={{ fontSize: '13px' }}>Rows per page:</span>
                                <Select
                                    size="small"
                                    sx={{
                                        maxHeight: '26px',
                                        fontSize: '13px', // This changes the font size of the displayed value
                                        '& .MuiSelect-select': {
                                            fontSize: '13px',
                                            padding: '4px 8px', // optional: adjust padding for tighter layout
                                        }
                                    }}
                                    MenuProps={{
                                        anchorOrigin: {
                                            vertical: 'top',       // Anchor at the top of the Select
                                            horizontal: 'left',
                                        },
                                        transformOrigin: {
                                            vertical: 'bottom',    // Menu grows upward from the bottom
                                            horizontal: 'left',
                                        },
                                        PaperProps: {
                                            sx: {
                                                fontSize: '13px',
                                            }
                                        }
                                    }}
                                    value={pageSize}
                                    onChange={(e) => {
                                        setPageSize(e.target.value);
                                        setCurrentPage(1); // Reset to first page
                                    }}
                                >
                                    {[6, 10, 20, 50, 100].map((size) => (
                                        <MenuItem size="small" key={size} value={size} sx={{ fontSize: '13px' }}>{size}</MenuItem>
                                    ))}
                                </Select>
                            </div>



                        </Stack>
                    </div>
                }
            </div>
        </Box>

    );
}

PostedFilterDataGrid.propTypes = {
    data: PropTypes.any.isRequired,
    headerName: PropTypes.any.isRequired,
};
